class Item11FeedbackForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.form = this.querySelector('[data-feedback-form]');
    this.summary = this.querySelector('[data-error-summary]');
    this.errorList = this.querySelector('[data-error-list]');
    this.status = this.querySelector('[data-form-status]');
    this.submitButton = this.querySelector('[data-submit-button]');
    this.submitLabel = this.querySelector('[data-submit-label]');
    this.stateGroup = this.querySelector('[data-state-group]');
    this.stateInput = this.form?.elements.namedItem('state');
    this.messages = this.readMessages();
    this.defaultSubmitLabel = this.submitLabel?.textContent || 'Submit feedback';
    this.storageKey = `ks:item11:submission:${window.location.pathname}`;
    this.acceptedStorageKey = `${this.storageKey}:accepted`;
    this.submissionKey = this.getSubmissionKey();
    this.abortController = new AbortController();

    if (!this.form || !this.submitButton) return;
    this.form.addEventListener('submit', (event) => this.handleSubmit(event), { signal: this.abortController.signal });
    this.form.addEventListener('change', (event) => this.handleChange(event), { signal: this.abortController.signal });
    this.form.addEventListener('input', (event) => this.clearFieldError(event.target), { signal: this.abortController.signal });
    this.updateStateField();

    if (window.sessionStorage.getItem(this.acceptedStorageKey) === this.submissionKey) {
      this.submitButton.disabled = true;
      this.setStatus(this.messages.alreadyAccepted);
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  readMessages() {
    try {
      return JSON.parse(this.querySelector('[data-feedback-messages]')?.textContent || '{}');
    } catch {
      return {};
    }
  }

  getSubmissionKey() {
    const existing = window.sessionStorage.getItem(this.storageKey);
    if (existing) return existing;
    const random = window.crypto?.randomUUID?.() || Array.from(window.crypto.getRandomValues(new Uint32Array(4)), (value) => value.toString(16)).join('-');
    const value = `feedback:${random}`;
    window.sessionStorage.setItem(this.storageKey, value);
    return value;
  }

  handleChange(event) {
    if (event.target?.name === 'name_display') this.updateStateField();
    this.clearFieldError(event.target);
  }

  updateStateField() {
    if (!this.form || !this.stateGroup || !this.stateInput) return;
    const selected = this.form.querySelector("input[name='name_display']:checked")?.value;
    const visible = selected === 'first_name_and_state';
    this.stateGroup.hidden = !visible;
    this.stateInput.disabled = !visible;
    this.stateInput.required = visible;
    if (!visible) {
      this.stateInput.value = '';
      this.clearFieldError(this.stateInput);
    }
  }

  clearErrors() {
    this.summary.hidden = true;
    this.errorList.replaceChildren();
    this.querySelectorAll('[data-error-for]').forEach((node) => { node.textContent = ''; });
    this.form.querySelectorAll('[aria-invalid="true"]').forEach((node) => node.removeAttribute('aria-invalid'));
  }

  clearFieldError(target) {
    if (!(target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)) return;
    const name = target.name;
    this.querySelector(`[data-error-for="${CSS.escape(name)}"]`)?.replaceChildren();
    this.form.querySelectorAll(`[name="${CSS.escape(name)}"]`).forEach((node) => node.removeAttribute('aria-invalid'));
  }

  collectValidationErrors() {
    this.updateStateField();
    const errors = [];
    const handled = new Set();
    for (const field of this.form.elements) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)) continue;
      if (!field.name || handled.has(field.name) || field.disabled) continue;
      handled.add(field.name);
      const group = [...this.form.querySelectorAll(`[name="${CSS.escape(field.name)}"]`)];
      const invalid = group.find((node) => !node.validity.valid);
      if (!invalid) continue;
      const message = invalid.name === 'state' && invalid.validity.valueMissing ? this.messages.stateRequired : invalid.validationMessage;
      errors.push({ field: invalid, name: invalid.name, message });
    }
    return errors;
  }

  showErrors(errors, summaryMessage) {
    this.clearErrors();
    if (summaryMessage) {
      const item = document.createElement('li');
      item.textContent = summaryMessage;
      this.errorList.append(item);
    }
    for (const error of errors) {
      const fields = this.form.querySelectorAll(`[name="${CSS.escape(error.name)}"]`);
      fields.forEach((field) => field.setAttribute('aria-invalid', 'true'));
      const inline = this.querySelector(`[data-error-for="${CSS.escape(error.name)}"]`);
      if (inline) inline.textContent = error.message;
      const item = document.createElement('li');
      const link = document.createElement('a');
      if (!error.field.id) error.field.id = `ks-feedback-error-target-${error.name}`;
      link.href = `#${error.field.id}`;
      link.textContent = error.message;
      link.addEventListener('click', (event) => {
        event.preventDefault();
        error.field.focus();
      }, { once: true });
      item.append(link);
      this.errorList.append(item);
    }
    this.summary.hidden = false;
    this.summary.focus();
  }

  value(name) {
    const field = this.form.elements.namedItem(name);
    if (field instanceof RadioNodeList) return field.value.trim();
    if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) return field.value.trim();
    return '';
  }

  payload() {
    const nullable = (name) => this.value(name) || null;
    const rating = this.value('experience_rating');
    return {
      submission_key: this.submissionKey,
      traveler_request_id: nullable('traveler_request_id'),
      full_name: this.value('full_name'),
      email: this.value('email'),
      stage_reached: nullable('stage_reached'),
      experience_rating: rating ? Number(rating) : null,
      helpful: nullable('helpful'),
      improvements: nullable('improvements'),
      comparison_easier: nullable('comparison_easier'),
      agency_feedback: nullable('agency_feedback'),
      publication_permission: this.value('publication_permission') === 'yes',
      editing_permission: this.value('editing_permission') === 'yes',
      authenticity_declaration: this.form.elements.namedItem('authenticity_declaration')?.checked === true,
      name_display: this.value('name_display'),
      state: nullable('state')
    };
  }

  setBusy(busy) {
    this.submitButton.disabled = busy;
    this.submitButton.setAttribute('aria-busy', String(busy));
    if (this.submitLabel) this.submitLabel.textContent = busy ? this.messages.submitting : this.defaultSubmitLabel;
  }

  setStatus(message) {
    this.status.textContent = message || '';
  }

  backendMessage(response, body) {
    if (response.status === 409) return this.messages.conflict;
    if (response.status === 429) return this.messages.rateLimited;
    if (response.status === 503) return this.messages.unavailable;
    if (response.status === 422 && body?.error === 'state_required_for_display') return this.messages.stateRequired;
    return this.messages.generic;
  }

  async handleSubmit(event) {
    event.preventDefault();
    if (this.submitting || window.sessionStorage.getItem(this.acceptedStorageKey) === this.submissionKey) return;
    this.clearErrors();
    this.setStatus('');
    const errors = this.collectValidationErrors();
    if (errors.length) {
      this.showErrors(errors, this.messages.validationSummary);
      return;
    }

    this.submitting = true;
    this.setBusy(true);
    const requestController = new AbortController();
    const timeout = window.setTimeout(() => requestController.abort('timeout'), 15000);
    try {
      const response = await fetch(this.dataset.endpoint, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(this.payload()),
        signal: requestController.signal
      });
      let body = null;
      try { body = await response.json(); } catch { body = null; }
      if ((response.status === 200 || response.status === 201) && body?.accepted === true) {
        window.sessionStorage.setItem(this.acceptedStorageKey, this.submissionKey);
        this.setStatus(this.messages.success);
        this.submitButton.disabled = true;
        window.setTimeout(() => window.location.assign(this.dataset.confirmationUrl), 700);
        return;
      }
      this.showErrors([], this.backendMessage(response, body));
    } catch (error) {
      const message = requestController.signal.aborted ? this.messages.timeout : this.messages.network;
      this.showErrors([], message);
    } finally {
      window.clearTimeout(timeout);
      this.submitting = false;
      if (window.sessionStorage.getItem(this.acceptedStorageKey) !== this.submissionKey) this.setBusy(false);
    }
  }
}

if (!customElements.get('item11-feedback-form')) customElements.define('item11-feedback-form', Item11FeedbackForm);
